<template>
    <div class="ssml-control">
        <h4 class="ssml-control__name">
            {{ tag.label[$i18n.locale] }}
        </h4>
        <div class="ssml-control__attributes">
            <!-- no attributes tag (quote, p, s) -->
            <div v-if="!attributesArray.length" class="ssml-control__attribute ssml-attribute">
                <Switcher :model-value="!!tagNode" @update:model-value="setNoAttributeTag">
                    <span class="ssml-attribute__label">{{ $t('misc.apply') }}</span>
                </Switcher>
            </div>
            <!-- attributes -->
            <template v-for="attribute in tag.attribute">
                <div
                    v-if="showAttribute(attribute)"
                    :key="attribute.name"
                    class="ssml-control__attribute"
                >
                    <!-- @todo add more in scope slot here -->
                    <slot :attribute="attribute" :tag="tag">
                        <div
                            v-if="attribute.type === 'multi_template'"
                            class="ssml-control__template"
                        >
                            <SsmlMultitemplateControl
                                :value="attributeValues[attribute.name]"
                                :attribute="attribute"
                                @update="setAttribute(attribute.name, $event)"
                            />
                        </div>
                        <div
                            v-else-if="attribute.type === 'template'"
                            class="ssml-control__template"
                        >
                            <SsmlTemplateControl
                                :value="attributeValues[attribute.name]"
                                :attribute="attribute"
                                @update="setAttribute(attribute.name, $event)"
                            />
                        </div>
                        <div v-else class="ssml-attribute">
                            <div class="ssml-attribute__label">
                                {{ getAttributeLabel(attribute) }}
                            </div>
                            <div class="ssml-attribute__input">
                                <FormInput
                                    v-if="attribute.type === 'input'"
                                    :model-value="attributeValues[attribute.name]"
                                    editable
                                    clearable
                                    :disabled="isParentRequired(attribute)"
                                    theme="editor"
                                    width="240px"
                                    @change="setAttribute(attribute.name, $event)"
                                />
                                <CustomSelect
                                    v-if="attribute.type === 'select'"
                                    :model-value="attributeValues[attribute.name]"
                                    :options="getAttributeOptions(attribute)"
                                    :form-value-text="
                                        item => getAttributeValueLabel(item, attribute)
                                    "
                                    :disabled="isParentRequired(attribute)"
                                    clearable
                                    @update:model-value="setAttribute(attribute.name, $event)"
                                >
                                    <template #default="{ item }">
                                        {{ getAttributeValueLabel(item, attribute) }}
                                    </template>
                                </CustomSelect>
                                <NumberInput
                                    v-if="attribute.type === 'number'"
                                    :model-value="attributeValues[attribute.name]"
                                    :step="attribute.step || 1"
                                    :disabled="isParentRequired(attribute)"
                                    :max="getLimitation(attribute, 'max')"
                                    :min="getLimitation(attribute, 'min')"
                                    @update:model-value="setAttribute(attribute.name, $event)"
                                />
                                <SsmlStressControl
                                    v-if="tag.name === 'speechki-accent'"
                                    :value="attributeValues[attribute.name]"
                                    :speaker="speaker"
                                    :nodes="nodes"
                                    @update="setAttribute(attribute.name, $event)"
                                />
                                <SsmlPhonemeControl
                                    v-if="attribute.name === 'ph'"
                                    :speaker="speaker"
                                    :nodes="nodes"
                                    :disabled="isParentRequired(attribute)"
                                    :tag="tag"
                                    @change="setAttribute(attribute.name, $event)"
                                />
                                <TokenInput
                                    v-if="attribute.type === 'token'"
                                    :model-value="attributeValues[attribute.name]"
                                    :tokens="getAttributeOptions(attribute)"
                                    :labels="attribute.labels"
                                    @update:model-value="setAttribute(attribute.name, $event)"
                                />
                            </div>
                        </div>
                    </slot>
                    <template v-if="attribute.optional && optionalAttributes(attribute)">
                        <div class="ssml-control__attribute">
                            <div
                                v-for="optAttr in optionalAttributes(attribute)"
                                :key="optAttr.name"
                                class="ssml-attribute"
                            >
                                <div v-if="!isTemplate(optAttr)" class="ssml-attribute__label">
                                    {{ getAttributeLabel(optAttr) }}
                                </div>
                                <div
                                    v-if="optAttr.type === 'multi_template'"
                                    class="ssml-control__template"
                                >
                                    <SsmlMultitemplateControl
                                        :value="attributeValues[optAttr.name]"
                                        :attribute="optAttr"
                                        @update="setAttribute(optAttr.name, $event, true)"
                                    />
                                </div>
                                <div
                                    v-else-if="optAttr.type === 'template'"
                                    class="ssml-control__template"
                                >
                                    <SsmlTemplateControl
                                        :value="attributeValues[optAttr.name]"
                                        :attribute="optAttr"
                                        @update="setAttribute(optAttr.name, $event, true)"
                                    />
                                </div>
                                <div class="ssml-attribute__input">
                                    <FormInput
                                        v-if="optAttr.type === 'input'"
                                        :model-value="attributeValues[optAttr.name]"
                                        editable
                                        clearable
                                        :disabled="isParentRequired(optAttr)"
                                        theme="editor"
                                        width="240px"
                                        @change="setAttribute(optAttr.name, $event, true)"
                                    />
                                    <CustomSelect
                                        v-if="
                                            optAttr.type === 'select' ||
                                                optAttr.type === 'multiselect'
                                        "
                                        :model-value="attributeValues[optAttr.name]"
                                        :options="getAttributeOptions(optAttr)"
                                        :form-value-text="
                                            item => getAttributeValueLabel(item, optAttr)
                                        "
                                        :disabled="isParentRequired(optAttr)"
                                        clearable
                                        @update:model-value="setAttribute(optAttr.name, $event, true)"
                                    >
                                        <template #default="{ item}">
                                            {{ getAttributeValueLabel(item, optAttr) }}
                                        </template>
                                    </CustomSelect>
                                    <NumberInput
                                        v-if="optAttr.type === 'number'"
                                        :model-value="attributeValues[optAttr.name]"
                                        :step="optAttr.step || 1"
                                        :disabled="isParentRequired(optAttr)"
                                        :max="getLimitation(optAttr, 'max')"
                                        :min="getLimitation(optAttr, 'min')"
                                        with-input
                                        @update:model-value="setAttribute(optAttr.name, $event, true)"
                                    />
                                    <TokenInput
                                        v-if="optAttr.type === 'token'"
                                        :model-value="attributeValues[optAttr.name]"
                                        :tokens="getAttributeOptions(optAttr)"
                                        :labels="optAttr.labels"
                                        @update:model-value="setAttribute(optAttr.name, $event, true)"
                                    />
                                </div>
                            </div>
                        </div>
                    </template>
                </div>
            </template>
        </div>
    </div>
</template>

<script>
import {
    FormInput,
    CustomSelect,
    NumberInput,
    TokenInput
} from '@speechki-frontend/shared/ui/forms';
import Switcher from '@speechki-frontend/shared/ui/Switcher';

import { clearFalsyValues } from '@speechki-frontend/shared/helpers/functions';

import SsmlPhonemeControl from '../SsmlPhonemeControl/SsmlPhonemeControl';
import SsmlTemplateControl from './SsmlTemplateControl';
import SsmlMultitemplateControl from './SsmlMultitemplateControl';
import SsmlStressControl from '../SsmlStressControl/SsmlStressControl';

import { getInitialValues } from './utils';

export default {
    name: 'SsmlBaseControl',
    components: {
        FormInput,
        CustomSelect,
        SsmlPhonemeControl,
        NumberInput,
        TokenInput,
        Switcher,
        SsmlTemplateControl,
        SsmlMultitemplateControl,
        SsmlStressControl
    },
    props: {
        tag: {
            type: Object,
            required: true
        },
        nodes: {
            type: Array,
            default: () => []
        },
        speaker: {
            type: String,
            required: true
        }
    },

    provide() {
        return {
            getAttributeLabel: this.getAttributeLabel,
            getAttributeOptions: this.getAttributeOptions,
            getAttributeValueLabel: this.getAttributeValueLabel,
            isParentRequired: this.isParentRequired,
            getLimitation: this.getLimitation
        };
    },

    data() {
        return {
            attributeValues: {}
        };
    },

    computed: {
        isSingle() {
            return this.nodes.length === 1;
        },

        isThisTag() {
            if (!this.isSingle) return false;

            return this.nodes[0].name === this.tag.name;
        },

        tagNode() {
            if (!this.isSingle || !this.isThisTag) {
                return undefined;
            }

            return this.nodes[0];
        },

        isBreakOrSpace() {
            return (
                this.isSingle && (this.nodes[0].name === 'space' || this.nodes[0].name === 'break')
            );
        },

        attributesEmpty() {
            return Object.values(this.attributeValues).every(attr => !attr);
        },

        attributesArray() {
            return Object.values(this.tag.attribute);
        },

        requiredAttributes() {
            return this.attributesArray.reduce((acc, attr) => {
                if (attr.parent && !attr.is_optional) {
                    acc.push(attr.parent, attr.name);
                }

                return acc;
            }, []);
        },

        hasRequiredAttributes() {
            return this.requiredAttributes.every(attr => !!this.attributeValues[attr]);
        },

        parentAttributeMap() {
            return this.attributesArray.reduce((map, attribute) => {
                if (!map[attribute.name]) {
                    map[attribute.name] = [];
                }

                if (attribute.parent) {
                    map[attribute.name].push(attribute.parent);
                }

                return map;
            }, {});
        }
    },

    created() {
        const values = getInitialValues(this.tag.attribute, this.tagNode);
        this.attributeValues = values;
    },

    methods: {
        getAttributeLabel(attribute) {
            const { label = { eng: attribute.name, rus: attribute.name } } = attribute;

            return label[this.$i18n.locale];
        },

        getAttributeValueLabel(value, attribute) {
            const { labels = {} } = attribute;

            return (labels[value] && labels[value][this.$i18n.locale]) || value;
        },

        getAttributeOptions(attribute) {
            const { ref_link: refLink, refs, values } = attribute;

            if (refLink && refs) {
                if (!this[refLink]) {
                    throw new Error('SsmlBaseControl: No neccessary ref in props');
                }

                const refValue = values[this[refLink]];

                return refs[refValue] || [];
            }

            return attribute.values;
        },

        isParentRequired(attribute) {
            return attribute.parent && !this.attributeValues[attribute.parent];
        },

        optionalAttributes(attribute) {
            if (this.attributeValues[attribute.name]) {
                return attribute.optional[this.attributeValues[attribute.name]];
            }

            return [];
        },

        getLimitation(attribute, prop) {
            const { limitations = { max_value: Infinity, min_value: -Infinity } } = attribute;

            return limitations[`${prop}_value`];
        },

        getParentAttributes(attributeName) {
            if (!this.parentAttributeMap[attributeName]) return {};

            return this.parentAttributeMap[attributeName].reduce((values, parent) => {
                values[parent] = this.attributeValues[parent];
                return values;
            }, {});
        },

        setAttribute(attributeName, value, isOptional = false) {
            if (this.attributeValues[attributeName] === value) return;

            let values = {
                ...this.getParentAttributes(attributeName),
                [attributeName]: value
            };

            if (isOptional) {
                values = {
                    ...this.attributeValues,
                    [attributeName]: value
                };
            }

            this.attributeValues = values;

            // no attributes
            if (this.attributesEmpty || !this.hasRequiredAttributes) {
                this.attributeValues = getInitialValues(this.tag.attribute, this.tagNode);
                this.unwrapNodes();
                return;
            }

            if (this.tagNode) {
                this.update({ attributes: clearFalsyValues(this.attributeValues) }, this.tagNode);
                return;
            }

            this.wrapNodes({
                attributes: {
                    ...clearFalsyValues(this.attributeValues)
                },
                type: 'tag',
                name: this.tag.name
            });
        },

        setNoAttributeTag(value) {
            if (!value) {
                this.unwrapNodes();
                return;
            }

            this.wrapNodes({
                attributes: {},
                type: 'tag',
                name: this.tag.name
            });
        },

        isTemplate({ type = '' }) {
            return type === 'template' || type === 'multi_template';
        },

        showAttribute(attribute) {
            return !this.tag.exclude_attributes.includes(attribute.name) && !attribute.hidden;
        },

        // parsing/build methods
        update(changes, node) {
            this.$emit('update', { changes, node });
        },
        wrapNodes(changes) {
            this.$emit('wrapNodes', changes);
        },
        unwrapNodes() {
            this.$emit('unwrapNodes');
        }
    }
};
</script>

<style src="./SsmlBaseControl.scss" lang="scss"></style>
